import React, { useCallback, useMemo, useEffect } from 'react';

import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { Router } from '@techstyle/next-routes';
import { useCustomer } from '@techstyle/react-accounts';
import { mobile, desktop, useBreakpoint } from '@techstyle/react-components';
import { useIntl, FormattedMessage } from '@techstyle/react-intl';

import {
  trackByoInterstitialClose,
  trackShowByoExitModal,
} from '../../actions/byo';
import { BasicButton } from '../../styles/button';
import { useByoContext } from '../ByoProvider';
import { Component as ByoStickyProgressBarDesktop } from '../ByoStickyProgressBarDesktop';
import { Component as ByoStickyProgressBarMobile } from '../ByoStickyProgressBarMobile';
import { Component as CloseButton } from '../CloseButton';

const Wrapper = styled.div``;

const StyledCloseButton = styled(CloseButton)`
  width: 40px;
  height: 40px;
  line-height: 40px;
`;

const PrimaryHeader = styled.div`
  position: relative;
  font-size: 18px;
  font-weight: bold;
  line-height: 24px;
  margin-bottom: 4px;
  letter-spacing: 0.4px;
  text-transform: capitalize;
  ${mobile`
    margin-bottom: ${({ theme }) => theme.spacing.tiny}px;
    &::before, &::after {
      content: '';
      position: absolute;
      top: 50%;
      border-bottom: 1px solid;
      width: 28px;
      margin: 0 12px;
    }
    &::before {
      right: 100%;
    }
    &::after {
      left: 100%;
    }
  `}
`;

const GuideText = styled.p`
  font-weight: 300;
  line-height: 17px;
  ${mobile`
    text-align: ${props => (props.isInterstitialFlow ? 'left' : 'center')};
  `}
  ${desktop`
    text-align: left;
  `}
`;

const ConfirmationText = styled.div`
  line-height: 17px;
  letter-spacing: 0.3px;
`;

const ModalCTA = styled(BasicButton).attrs({
  fullWidth: true,
  buttonStyle: css`
    width: 100%;
    margin-top: 12px;
    border-color: ${({ theme }) => theme.colors.active};
    background-color: ${({ theme }) => theme.colors.active};
  `,
})``;

const InterstitialGuideText = styled.h5`
  align-self: start;
  line-height: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding-bottom: 24px;
`;

const ByoInterstitialBundleLabel = styled.span`
  color: ${({ theme }) => theme.colors.promo};
  font-weight: 700;
`;

const ByoInterstitialBundleLabelUppercase = styled(ByoInterstitialBundleLabel)`
  text-transform: uppercase;
`;

const ByoInterstitialBundleLabelUppercaseBlack = styled(
  ByoInterstitialBundleLabel
)`
  color: ${({ theme }) => theme.colors.default};
`;

export default function ByoStickyProgressBar({ isGridPage = false }) {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const { isMobile } = useBreakpoint();
  const profile = useCustomer();
  const {
    currentStep,
    exitByoFlow,
    firstProductMpid,
    isInterstitialFlow,
    isLastStep,
    isSetCompleted,
    lastAddedItem,
    selectedByoSet,
    setIsExitModalOpen,
    setIsProgressBarModalOpen,
  } = useByoContext();
  const { startCategory, combinationOptions } = selectedByoSet;
  const orderedSetConfig = [startCategory, ...combinationOptions];
  const currentCategory = orderedSetConfig[currentStep - 1];
  // hide progress bar modal when unmount
  useEffect(() => {
    return () => setIsProgressBarModalOpen(false);
  }, [setIsProgressBarModalOpen]);

  const handleModalCTAClick = useCallback(() => {
    if (currentCategory?.byoGridsUrl) {
      Router.push(
        `${currentCategory.byoGridsUrl}?lastMasterProductId=${lastAddedItem.master_product_id}`
      );
    }
  }, [currentCategory, lastAddedItem.master_product_id]);

  const handleShowExitModal = useCallback(() => {
    setIsExitModalOpen(true);
    dispatch(trackShowByoExitModal("Progress bar 'x'"));
  }, [dispatch, setIsExitModalOpen]);

  const onExitButtonClick = useCallback(() => {
    dispatch(
      trackByoInterstitialClose({
        masterProductId: firstProductMpid,
        profile,
      })
    );
    if (isInterstitialFlow && currentStep <= 2) {
      Router.push('/cart').then(() => exitByoFlow());
    } else {
      handleShowExitModal();
    }
  }, [
    currentStep,
    dispatch,
    exitByoFlow,
    firstProductMpid,
    handleShowExitModal,
    isInterstitialFlow,
    profile,
  ]);

  const buildYourSetTitle = useMemo(
    () => (
      <PrimaryHeader data-autotag="progress_bar_header">
        Your {selectedByoSet?.label}:
      </PrimaryHeader>
    ),
    [selectedByoSet]
  );

  const interstitialTitleText = useMemo(
    () =>
      isInterstitialFlow && currentStep <= 2 ? (
        <InterstitialGuideText>
          <FormattedMessage
            id="site_byo.byo_interstitial_before_you_go"
            defaultMessage="Before you go..."
          />
        </InterstitialGuideText>
      ) : null,
    [currentStep, isInterstitialFlow]
  );

  const confirmationText = useMemo(
    () => (
      <ConfirmationText>
        <FormattedMessage
          id="site_byo.item_added_to_set"
          defaultMessage="{name} added to your set"
          values={{
            name: <strong>{lastAddedItem.label}</strong>,
          }}
        />
      </ConfirmationText>
    ),
    [lastAddedItem.label]
  );

  const guideText = useMemo(() => {
    if (!currentCategory?.categoryRb) {
      return null;
    }

    const getText = () => {
      if (isInterstitialFlow) {
        if (currentStep <= 2) {
          return (
            <FormattedMessage
              id="site_byo.byo_interstitial_progress_header"
              defaultMessage="Turn this item into a {selectedByoSet} for 1 Member Credit"
              values={{
                selectedByoSet: (
                  <ByoInterstitialBundleLabel>
                    {selectedByoSet?.label}
                  </ByoInterstitialBundleLabel>
                ),
              }}
            />
          );
        } else if (isSetCompleted) {
          return (
            <ByoInterstitialBundleLabelUppercase>
              <FormattedMessage
                id="site_byo.byo_interstitial_your_bundle"
                defaultMessage="Your {bundle}{punctuation}"
                values={{
                  bundle: selectedByoSet?.label,
                  punctuation: '!',
                }}
              />
            </ByoInterstitialBundleLabelUppercase>
          );
        } else {
          return (
            <ByoInterstitialBundleLabelUppercaseBlack>
              <FormattedMessage
                id="site_byo.byo_interstitial_your_bundle"
                defaultMessage="Your {bundle}{punctuation}"
                values={{
                  bundle: selectedByoSet?.label,
                  punctuation: '...',
                }}
              />
            </ByoInterstitialBundleLabelUppercaseBlack>
          );
        }
      }
      if (isLastStep) {
        return (
          <FormattedMessage
            id="site_byo.complete_set_text"
            defaultMessage="Add {categoryRb} to complete your {count}-item set"
            values={{
              categoryRb: formatMessage(currentCategory.categoryRb),
              count: orderedSetConfig.length,
            }}
          />
        );
      } else {
        return (
          <FormattedMessage
            id="site_byo.add_to_set_text"
            defaultMessage="Add {categoryRb} to your {count}-item set"
            values={{
              categoryRb: formatMessage(currentCategory.categoryRb),
              count: orderedSetConfig.length,
            }}
          />
        );
      }
    };

    if (currentCategory?.categoryRb) {
      return (
        <GuideText
          data-autotag="progress_bar_text"
          isInterstitialFlow={isInterstitialFlow}
        >
          {getText()}
        </GuideText>
      );
    }

    return null;
  }, [
    currentCategory,
    currentStep,
    formatMessage,
    isInterstitialFlow,
    isLastStep,
    isSetCompleted,
    orderedSetConfig.length,
    selectedByoSet.label,
  ]);

  const closeButton = useMemo(
    () => (
      <StyledCloseButton
        iconSize={10}
        data-autotag="progress_bar_close"
        onClick={onExitButtonClick}
      />
    ),
    [onExitButtonClick]
  );

  const modalCTA = useMemo(
    () =>
      currentCategory?.categoryRb ? (
        <ModalCTA
          onClick={handleModalCTAClick}
          data-autotag=" progress_bar_cta"
        >
          <FormattedMessage
            id="site_byo.progress_bar_modal_cta"
            defaultMessage="Add {categoryRb}"
            values={{
              categoryRb: formatMessage(currentCategory.categoryRb),
            }}
          />
        </ModalCTA>
      ) : null,
    [currentCategory, formatMessage, handleModalCTAClick]
  );

  return (
    <Wrapper>
      {isMobile ? (
        <ByoStickyProgressBarMobile
          buildYourSetTitle={buildYourSetTitle}
          confirmationText={confirmationText}
          guideText={guideText}
          closeButton={closeButton}
          modalCTA={modalCTA}
          isGridPage={isGridPage}
          onExitButtonClick={onExitButtonClick}
          interstitialTitleText={interstitialTitleText}
        />
      ) : (
        <ByoStickyProgressBarDesktop
          buildYourSetTitle={buildYourSetTitle}
          confirmationText={confirmationText}
          guideText={guideText}
          closeButton={closeButton}
          modalCTA={modalCTA}
          isGridPage={isGridPage}
          onExitButtonClick={onExitButtonClick}
          interstitialTitleText={interstitialTitleText}
        />
      )}
    </Wrapper>
  );
}

ByoStickyProgressBar.propTypes = {
  isGridPage: PropTypes.bool,
};
